// Compact outline of the host page's visible structure – the real headings,
// buttons, navigation entries, content links, list items, and table content
// the user can see. The chatbot uses it to ground its first questions in what
// is actually on screen: answer directly when the item the user names is
// already listed ("politique de frais" in a document list), and otherwise ask
// location questions with the page's actual element names ("the Demandes
// actives table") instead of generic examples ("a table"). Bounded so the
// payload stays a lightweight postMessage message.
//
// The widget's own surfaces are excluded by construction: the FAB lives in a
// closed shadow root that `document.querySelectorAll` cannot reach, and the
// iframe carries no host-page text. Hidden elements and those the host
// excluded from session replay are skipped, the same as in `page-text.ts`.

import { isExcludedFromPageContext } from "./page-text";

const MAX_ITEMS_PER_KIND = 12;
const MAX_LABEL_LENGTH = 60;
const MAX_OUTLINE_LENGTH = 1500;

const clean = (raw: string | null | undefined): string =>
	(raw ?? "").replaceAll(/\s+/gu, " ").trim().slice(0, MAX_LABEL_LENGTH);

function collectLabels(selector: string, seen: Set<string>): string[] {
	const labels: string[] = [];
	for (const element of document.querySelectorAll(selector)) {
		if (
			!(element instanceof HTMLElement) ||
			isExcludedFromPageContext(element)
		) {
			continue;
		}
		const label = clean(
			element.getAttribute("aria-label") ??
				(element instanceof HTMLInputElement
					? element.value
					: element.textContent),
		);
		if (label !== "" && !seen.has(label)) {
			seen.add(label);
			labels.push(label);
			if (labels.length >= MAX_ITEMS_PER_KIND) {
				break;
			}
		}
	}
	return labels;
}

/**
 * Describe what the host page currently shows, one labelled line per element
 * kind. Returns an empty string when nothing useful is found so callers can
 * omit the field entirely.
 */
export function collectPageOutline(): string {
	// Content kinds (links, list items, table rows) capture the page's actual
	// data – document names, row titles – so the agent can see the item a user
	// asks about instead of asking them to locate it. A list item is usually a
	// link's parent, so the shared `seen` set keeps each label on one line.
	const kinds: readonly (readonly [string, string])[] = [
		["Sections", "h1, h2, h3"],
		["Buttons", 'button, [role="button"], input[type="submit"]'],
		["Navigation", "nav a"],
		["Links", "a[href]:not(nav a)"],
		["List items", "li:not(nav li)"],
		["Table columns", "th"],
		["Table rows", "td:first-child"],
	];
	const seen = new Set<string>();
	const lines: string[] = [];
	for (const [label, selector] of kinds) {
		const labels = collectLabels(selector, seen);
		if (labels.length > 0) {
			lines.push(`${label}: ${labels.join(" | ")}`);
		}
	}
	return lines.join("\n").slice(0, MAX_OUTLINE_LENGTH);
}
